/**
 * Where the pay page's API lives.
 *
 * Extracted from App.js, which had this hostname sniffing inline in a useEffect. It is here
 * because PaymentForm needs the same base URL to mint its gateway session and to poll the
 * payment's state, and two copies of a rule like this drift.
 *
 * Note what it is NOT used for: the origin allowed to confirm a payment by postMessage. That
 * comes from the server (`message_origin`), because this function is a *guess* -- it infers
 * the API host from the browser's own hostname, and the moment the configured `commandUrl`
 * is anything else, every payment would stop confirming with nothing logged anywhere.
 */

export const isLocalNetwork = (hostname = window.location.hostname) =>
  ['localhost', '127.0.0.1', '', '::1'].includes(hostname) ||
  hostname.startsWith('127.') ||
  hostname.startsWith('192.168.') ||
  hostname.startsWith('172.22.') || // 172.16.0.0 - 172.31.255.255
  hostname.startsWith('10.') ||
  hostname.endsWith('.local')

export const apiBase = () => {
  // An explicit build-time override beats the sniff. The sniff stays as the fallback so
  // nothing that builds this app today has to change.
  const configured = process.env.REACT_APP_API

  if (configured) {
    return configured.replace(/\/+$/, '')
  }

  return 'https://' + (isLocalNetwork() ? 'dev.linet.org.il' : 'app.linet.org.il')
}

/**
 * The payment hash out of the URL, or null if this is not a payment link.
 *
 * The path used to be concatenated straight into the backend URL, so whatever was in the
 * address bar became part of the request line -- and an empty path produced a request for
 * `/paymentreq/load/` that could only fail after a round trip. A hash is what the backend
 * generates: `varchar(12)`, alphanumeric. Anything else is not a link we issued.
 */
export const payHash = (pathname = window.location.pathname) => {
  if (typeof pathname !== 'string') {
    return null
  }

  const match = pathname.match(/^\/([A-Za-z0-9]{6,12})\/?$/)

  return match ? match[1] : null
}
